import React from 'react';
import Icon from '../../../components/AppIcon';

const RadiusSelector = ({ radius, onRadiusChange, minRadius = 1, maxRadius = 25 }) => {
  const handleSliderChange = (e) => {
    onRadiusChange(parseFloat(e?.target?.value));
  };

  const handleIncrement = () => {
    if (radius < maxRadius) {
      onRadiusChange(Math.min(radius + 1, maxRadius));
    }
  };

  const handleDecrement = () => {
    if (radius > minRadius) {
      onRadiusChange(Math.max(radius - 1, minRadius));
    }
  };

  const percentage = ((radius - minRadius) / (maxRadius - minRadius)) * 100;

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg">
            <Icon name="Radar" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-base md:text-lg font-semibold text-foreground">
              Search Radius
            </h3>
            <p className="caption text-muted-foreground">
              Adjust distance from midpoint
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDecrement}
            disabled={radius <= minRadius}
            className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-smooth active-press focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
            aria-label="Decrease radius"
          >
            <Icon name="Minus" size={16} />
          </button>
          <div className="flex flex-col items-center min-w-[80px] md:min-w-[100px]">
            <span className="font-heading text-2xl md:text-3xl font-bold text-primary data-text">
              {radius}
            </span>
            <span className="caption text-muted-foreground">miles</span>
          </div>
          <button
            onClick={handleIncrement}
            disabled={radius >= maxRadius}
            className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-smooth active-press focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
            aria-label="Increase radius"
          >
            <Icon name="Plus" size={16} />
          </button>
        </div>
      </div>

      <div className="relative">
        <input
          type="range"
          min={minRadius}
          max={maxRadius}
          step="0.5"
          value={radius}
          onChange={handleSliderChange}
          className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
          style={{
            background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${percentage}%, var(--color-muted) ${percentage}%, var(--color-muted) 100%)`
          }}
          aria-label="Radius slider"
          aria-valuemin={minRadius}
          aria-valuemax={maxRadius}
          aria-valuenow={radius}
        />
        <div className="flex justify-between mt-2">
          <span className="caption text-muted-foreground">{minRadius} mi</span>
          <span className="caption text-muted-foreground">{maxRadius} mi</span>
        </div>
      </div>

      <div className="mt-4 md:mt-6 p-3 md:p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs md:text-sm text-muted-foreground">
            Expanding the radius will show more venue options while maintaining equal travel times for all participants.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RadiusSelector;